import { ApptScheduleSteps, DesignMembers } from '@/constants';
import { Appointment, ApptBuyer, Buyer, HomeSummary, Member, Role, ScheduleApptDetails, Slot, TeamMember, Timezone } from '@/types';
import { setDateFormat } from '@/util';

const componentSelector = (currentStep: string, TeamMember: any, Calendar: any, Timeslots: any) => {
  switch (currentStep) {
    case ApptScheduleSteps.SELECT_TEAM_MEMBER:
      return TeamMember;

    case ApptScheduleSteps.SELECT_DATE:
      return Calendar;

    case ApptScheduleSteps.SELECT_TIME:
      return Timeslots;

    default:
      return '';
  }
};

const getAppointmentRequest = (appointment: Appointment, appointmentDetails: ScheduleApptDetails, buyer: Buyer, job: HomeSummary, buyers: ApptBuyer[], isModifyMode: boolean) => {
  const { appointmentType, appointmentId, createdBy } = appointment;
  const previousMemberEmail = appointment.teamMember?.emailId;
  const { teamMember, date, timeSlot, timeZone, location } = appointmentDetails;
  return {
    appointmentId: appointment.appointmentId || '',
    appointmentType: {
      ...appointmentType,
      allowDivisionTeamMembers: !!appointmentType.allowDivisionTeamMembers,
      buffer: appointmentType.buffer || +process.env.VUE_APP_APPOINTMENT_BUFFER_CONFIG
    },
    teamMember: {
      teamMemberId: teamMember[0].userId.toString(),
      firstName: teamMember[0].firstName,
      lastName: teamMember[0].lastName,
      group: teamMember[0].teamGroup,
      title: teamMember[0].title,
      emailId: teamMember[0].email,
      salesforceId: teamMember[0].salesforceId
    },
    buyer: {
      contactId: buyer.contactId,
      email: buyer.email,
      firstName: buyer.firstName,
      lastName: buyer.lastName
    },
    appointmentDate: setDateFormat(date),
    appointmentStartTime: timeSlot.start,
    appointmentEndTime: timeSlot.end,
    appointmentTimezone: timeZone.value,
    jobNumber: job.jobNumber,
    division: job.divisionName,
    cancelEmail: previousMemberEmail && previousMemberEmail !== teamMember[0].email ? previousMemberEmail : '',
    isOptionalAppointmentScheduled: !!(appointmentId && !isModifyMode),
    locationAddress: location,
    createdBy: {
      name: createdBy?.name || `${buyer.firstName} ${buyer.lastName}`,
      email: createdBy?.email || buyer.email,
      id: createdBy?.id || buyer.contactId,
      application: createdBy?.application || 'PORTAL'
    },
    updatedBy: {
      name: `${buyer.firstName} ${buyer.lastName}`,
      email: buyer.email,
      id: buyer.contactId,
      application: 'PORTAL'
    },
    neighborhoodId: job.neighborhoodCode || '',
    neighborhoodName: job.neighborhoodName,
    buyers
  };
}

const findTeamMemberById = (teamMembers: Member[], teamMemberId: number): Member => teamMembers.find((member) => member.userId === teamMemberId) || {} as Member;

const mapAppointmentData = (appointment: Appointment, teamMembers: Member[], timeZones: Timezone[]): ScheduleApptDetails => {
  const teamMember = findTeamMemberById(teamMembers, +appointment.teamMember.teamMemberId);
  const slots: Slot = {
    start: appointment.appointmentStartTime,
    end: appointment.appointmentEndTime,
    participants: []
  };
  const timeZone = timeZones.find(zone => zone.value.toLowerCase() === appointment.appointmentTimezone.toLowerCase())!;
  const initial = appointment.appointmentDate.split(/\//);
  const dateStr = [initial[2], initial[1], initial[0]].join('-');
  return {
    date: dateStr,
    teamMember: teamMember.userId ? [teamMember] : [],
    timeZone,
    timeSlot: slots,
    location: appointment.locationAddress
  };
}

const convertTeamMemberData = (distinctEntries: TeamMember[], teamMembers: Member[]): Member[] => {
  const memberArr = [] as Member[];
  distinctEntries.forEach(data => {
    const member = findTeamMemberById(teamMembers, +data.teamMemberId);
    if (member.userId) {
      memberArr.push(member);
    }
  });
  return memberArr;
}

const scheduleSteps = [ApptScheduleSteps.SELECT_TEAM_MEMBER, ApptScheduleSteps.SELECT_DATE, ApptScheduleSteps.SELECT_TIME, ApptScheduleSteps.APPOINTMENT_SCHEDULED];

const sortTeamMembersPerRole = (teamMembers: Member[]) => {
  const sortedList = [] as Member[];
  const sortOrder = [DesignMembers.SENIOR_DESIGN_CONSULTANT.toLowerCase(), DesignMembers.DESIGN_CONSULTANT.toLowerCase(), DesignMembers.DESIGN_ASSISTANT.toLowerCase()];
  sortOrder.forEach(order => {
    const results = teamMembers.filter((member) => member.title.toLowerCase() === order);
    results.forEach(item => {
      sortedList.push(item);
    });
  });
  const difference = teamMembers.filter(x => !sortedList.includes(x));
  return sortedList.concat(difference);
}

const getTeamMembersOfDesignRoles = (roles: Role[], teamMembers: Member[]) => {
  const eligibleRoles = roles.filter(role => [DesignMembers.DESIGN_CONSULTANT.toLowerCase(), DesignMembers.SENIOR_DESIGN_CONSULTANT.toLowerCase()].includes(role.fullName.toLowerCase()));
  const eligibleRoleNames = eligibleRoles.map((role) => role.fullName.toLowerCase());
  let designMembers = teamMembers.filter(member => eligibleRoleNames.includes(member.title.toLowerCase()));
  const designAssistantEligible = roles.findIndex(role => role.fullName.toLowerCase() === DesignMembers.DESIGN_ASSISTANT.toLowerCase());
  if (designAssistantEligible > -1 && designMembers.length) {
    const designAssistants = teamMembers.filter(member => member.title.toLowerCase() === DesignMembers.DESIGN_ASSISTANT.toLowerCase());
    designMembers = designMembers.concat(designAssistants)
  }
  return designMembers;
}

const setAssignedMembersOfStage = (designMembers: Member[], stageTeamMembers: Member[], currentMemberId: string) => {
  let teamMembers = designMembers;
  const currentlySelectedMember = stageTeamMembers.find((member) => member.userId === +currentMemberId);
  if (currentMemberId && currentlySelectedMember) {
    teamMembers.push(currentlySelectedMember);
  } else if (stageTeamMembers.length) {
    teamMembers.push(stageTeamMembers[0]);
  }
  teamMembers = Array.from(new Set(teamMembers.map((member) => member)));
  return sortTeamMembersPerRole(teamMembers);
}

const hideCalendarPreviousMonth = (selectedMonth: number) => {
  const prevIcon = document.querySelector('.is-left') as HTMLElement;
  if (new Date().getMonth() === selectedMonth) {
    if (prevIcon) {
      prevIcon.classList.add('is-disabled');
      prevIcon.style.opacity = '0.25';
      prevIcon.style.pointerEvents = 'none';
      prevIcon.style.cursor = 'not-allowed';
    }
  } else {
    prevIcon.classList.remove('is-disabled');
    prevIcon.style.opacity = '1';
    prevIcon.style.pointerEvents = '';
    prevIcon.style.cursor = '';
  }
}

export { componentSelector, getAppointmentRequest, scheduleSteps, mapAppointmentData, convertTeamMemberData, sortTeamMembersPerRole, getTeamMembersOfDesignRoles, setAssignedMembersOfStage, hideCalendarPreviousMonth };
